/** User actions whose UI freeze is measured in production (logged as `client_interaction`). */
export const CLIENT_INTERACTIONS = [
  "monthly_forecast_save",
  "monthly_forecast_revert",
  "adjustment_add",
  "adjustment_update",
  "adjustment_delete",
  // Another user's (or a system) forecast edit applied in this tab.
  "remote_forecast_update",
] as const
export type ClientInteraction = (typeof CLIENT_INTERACTIONS)[number]

type Script = {
  duration: number
  sourceFunctionName?: string
  invoker?: string
  sourceURL?: string
}
type Frame = { startTime: number; duration: number; scripts?: Script[] }

export type InteractionMeasurement = {
  blockedMs: number
  longestMs: number
  settledMs: number
  frames: number
  truncated: boolean
  topScript: string | null
}

// Saves land in stages (the mutation, the cache refresh, then the returns event), so a measurement
// waits out a few quiet seconds before deciding the page has settled.
const MIN_MS = 5_000
const QUIET_MS = 3_000
const MAX_MS = 60_000

/** What the long frames recorded after `start` say about the freeze. */
export function summarizeFrames(
  frames: Frame[],
  start: number,
  truncated: boolean,
): InteractionMeasurement {
  let blockedMs = 0
  let longestMs = 0
  let lastEnd = start
  let top: Script | undefined
  for (const frame of frames) {
    blockedMs += frame.duration
    longestMs = Math.max(longestMs, frame.duration)
    lastEnd = Math.max(lastEnd, frame.startTime + frame.duration)
    for (const script of frame.scripts ?? [])
      if (!top || script.duration > top.duration) top = script
  }
  return {
    blockedMs: Math.round(blockedMs),
    longestMs: Math.round(longestMs),
    settledMs: Math.round(lastEnd - start),
    frames: frames.length,
    truncated,
    topScript: top
      ? `${top.sourceFunctionName || top.invoker || "?"} ${top.sourceURL?.split("/").pop()?.split("?")[0] ?? ""}`
          .trim()
          .slice(0, 200)
      : null,
  }
}

// One measurement at a time: a second action inside the first's window ends it, so overlapping
// windows never count the same frames twice. A remote update never cuts short the user's own action.
let active: { remote: boolean; end: () => void } | undefined

/**
 * Watches the main thread from now until it has been quiet for a few seconds, then reports how
 * long it was blocked. Long Animation Frames (Chrome 123+) also name the script that blocked;
 * browsers with neither that nor long-task timing report nothing.
 */
export function measureInteraction(
  report: (measurement: InteractionMeasurement) => void,
  remote = false,
): void {
  if (typeof PerformanceObserver === "undefined") return
  const type = ["long-animation-frame", "longtask"].find((t) =>
    PerformanceObserver.supportedEntryTypes?.includes(t),
  )
  if (!type) return
  if (remote && active && !active.remote) return
  active?.end()
  const start = performance.now()
  const frames: Frame[] = []
  const observer = new PerformanceObserver((list) => {
    frames.push(...(list.getEntries() as unknown as Frame[]))
  })
  observer.observe({ type, buffered: false })
  const finish = (truncated: boolean) => {
    clearInterval(timer)
    observer.disconnect()
    active = undefined
    report(
      summarizeFrames(
        frames.filter((f) => f.startTime >= start),
        start,
        truncated,
      ),
    )
  }
  const timer = setInterval(() => {
    const now = performance.now()
    const lastEnd = frames.reduce((end, f) => Math.max(end, f.startTime + f.duration), start)
    const truncated = now - start >= MAX_MS
    if (!truncated && (now - start < MIN_MS || now - lastEnd < QUIET_MS)) return
    finish(truncated)
  }, 500)
  active = { remote, end: () => finish(true) }
}
